<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import * as XLSX from 'xlsx';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  list: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isLoading = ref(false);
const isSearching = ref(false);
const isAddingContacts = ref(false);
const isImporting = ref(false);
const importFileRef = ref(null);
const searchQuery = ref('');
const selectedLabel = ref(null);
const selectedCustomAttribute = ref(null);
const customAttributeValue = ref('');
const contacts = ref([]);
const searchResults = ref([]);
const selectedContacts = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const searchCurrentPage = ref(1);
const searchTotalPages = ref(1);
const searchTotalCount = ref(0);

const labels = computed(() => store.getters['labels/getLabels'] || []);
const customAttributes = computed(() => store.getters['attributes/getContactAttributes'] || []);

const labelOptions = computed(() => [
  { value: null, label: t('CONTACT_LISTS.CONTACTS_DIALOG.ALL_LABELS') },
  ...labels.value.map(label => ({
    value: label.title,
    label: label.title,
  })),
]);

const customAttributeOptions = computed(() => [
  { value: null, label: t('CONTACT_LISTS.CONTACTS_DIALOG.ALL_ATTRIBUTES') },
  ...customAttributes.value.map(attr => ({
    value: attr.attributeKey,
    label: attr.attributeDisplayName,
    displayType: attr.attributeDisplayType,
    values: attr.attributeValues || [],
  })),
]);

const selectedAttributeConfig = computed(() => {
  if (!selectedCustomAttribute.value) return null;
  return customAttributeOptions.value.find(
    opt => opt.value === selectedCustomAttribute.value
  );
});

const isListAttribute = computed(() => {
  return selectedAttributeConfig.value?.displayType === 'list';
});

const listAttributeOptions = computed(() => {
  if (!isListAttribute.value) return [];
  return selectedAttributeConfig.value?.values?.map(val => ({
    value: val,
    label: val,
  })) || [];
});

const dialogTitle = computed(() =>
  `${t('CONTACT_LISTS.CONTACTS_DIALOG.TITLE')} - ${props.list?.name}`
);

const allSearchSelected = computed(() => {
  if (searchResults.value.length === 0) return false;
  const availableContacts = searchResults.value.filter(c => !isContactInList(c));
  return availableContacts.length > 0 && availableContacts.every(c => selectedContacts.value.includes(c.id));
});

const someSearchSelected = computed(() => {
  if (searchResults.value.length === 0) return false;
  const availableContacts = searchResults.value.filter(c => !isContactInList(c));
  return availableContacts.some(c => selectedContacts.value.includes(c.id)) && !allSearchSelected.value;
});

const selectedCount = computed(() => selectedContacts.value.length);

const fetchContacts = async (page = 1) => {
  if (!props.list) return;

  isLoading.value = true;
  try {
    const response = await store.dispatch('contactLists/getContacts', {
      listId: props.list.id,
      page,
    });
    contacts.value = response.contacts || [];
    currentPage.value = response.meta?.current_page || 1;
    totalPages.value = response.meta?.total_pages || 1;
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const searchContacts = async (page = 1) => {
  isSearching.value = true;
  try {
    let data;

    // If custom attribute filter is selected, use filter API
    if (selectedCustomAttribute.value && customAttributeValue.value) {
      // Ensure we pass the string value, not an object
      const attributeValue = typeof customAttributeValue.value === 'object'
        ? customAttributeValue.value.value || customAttributeValue.value
        : customAttributeValue.value;

      const queryPayload = {
        payload: [
          {
            attribute_key: selectedCustomAttribute.value,
            filter_operator: 'equal_to',
            values: [attributeValue],
            custom_attribute_type: 'contact_attribute',
          },
        ],
      };

      const response = await ContactAPI.filter(page, 'name', queryPayload);
      data = response.data;
    } else {
      // Use regular search
      const response = await ContactAPI.search(
        searchQuery.value || '',
        page,
        'name',
        selectedLabel.value || ''
      );
      data = response.data;
    }

    searchResults.value = data.payload || [];
    searchCurrentPage.value = data.meta?.current_page || 1;
    searchTotalPages.value = data.meta?.total_pages || 1;
    searchTotalCount.value = data.meta?.count || 0;
  } catch (error) {
    searchResults.value = [];
    searchTotalCount.value = 0;
  } finally {
    isSearching.value = false;
  }
};

const toggleContactSelection = (contactId) => {
  const index = selectedContacts.value.indexOf(contactId);
  if (index === -1) {
    selectedContacts.value.push(contactId);
  } else {
    selectedContacts.value.splice(index, 1);
  }
};

const toggleSelectAllSearch = () => {
  const availableContacts = searchResults.value.filter(c => !isContactInList(c));
  if (allSearchSelected.value) {
    // Deselect all
    availableContacts.forEach(c => {
      const index = selectedContacts.value.indexOf(c.id);
      if (index !== -1) {
        selectedContacts.value.splice(index, 1);
      }
    });
  } else {
    // Select all
    availableContacts.forEach(c => {
      if (!selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
      }
    });
  }
};

// Select ALL contacts from the current search/filter (all pages, not just current)
const selectAllFromSearch = async () => {
  isSearching.value = true;
  try {
    let allContacts = [];
    let page = 1;
    let hasMore = true;

    // If custom attribute filter is selected
    if (selectedCustomAttribute.value && customAttributeValue.value) {
      // Ensure we pass the string value, not an object
      const attributeValue = typeof customAttributeValue.value === 'object'
        ? customAttributeValue.value.value || customAttributeValue.value
        : customAttributeValue.value;

      const queryPayload = {
        payload: [
          {
            attribute_key: selectedCustomAttribute.value,
            filter_operator: 'equal_to',
            values: [attributeValue],
            custom_attribute_type: 'contact_attribute',
          },
        ],
      };

      while (hasMore && page <= 50) { // Limit to 50 pages
        const response = await ContactAPI.filter(page, 'name', queryPayload);
        const data = response.data;
        const contactsList = data.payload || [];
        allContacts = [...allContacts, ...contactsList];
        // Continue if we got results and there might be more pages
        const totalPages = data.meta?.total_pages;
        hasMore = contactsList.length > 0 && (totalPages ? page < totalPages : true);
        page++;
      }
    } else if (selectedLabel.value) {
      // If label filter is selected
      while (hasMore && page <= 50) {
        const response = await ContactAPI.search('', page, 'name', selectedLabel.value);
        const data = response.data;
        const contactsList = data.payload || [];
        allContacts = [...allContacts, ...contactsList];
        // Continue if we got results and there might be more pages
        const totalPages = data.meta?.total_pages;
        hasMore = contactsList.length > 0 && (totalPages ? page < totalPages : true);
        page++;
      }
    } else {
      // Regular search
      while (hasMore && page <= 50) {
        const response = await ContactAPI.search(searchQuery.value || '', page, 'name', '');
        const data = response.data;
        const contactsList = data.payload || [];
        allContacts = [...allContacts, ...contactsList];
        // Continue if we got results and there might be more pages
        const totalPages = data.meta?.total_pages;
        hasMore = contactsList.length > 0 && (totalPages ? page < totalPages : true);
        page++;
      }
    }

    // Add all contacts that are not already in the list
    let addedCount = 0;
    allContacts.forEach(c => {
      if (!isContactInList(c) && !selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
        addedCount++;
      }
    });

    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_ALL_SEARCH', { count: addedCount }));
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const selectAllFromLabel = async () => {
  if (!selectedLabel.value) return;

  isSearching.value = true;
  try {
    // Fetch all contacts with the selected label (up to a reasonable limit)
    let allContacts = [];
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= 50) { // Limit to 50 pages
      const response = await ContactAPI.search(
        '',
        page,
        'name',
        selectedLabel.value
      );
      const data = response.data;
      const contactsList = data.payload || [];
      allContacts = [...allContacts, ...contactsList];
      // Continue if we got results and there might be more pages
      const totalPages = data.meta?.total_pages;
      hasMore = contactsList.length > 0 && (totalPages ? page < totalPages : true);
      page++;
    }

    // Add all contacts that are not already in the list
    let addedCount = 0;
    allContacts.forEach(c => {
      if (!isContactInList(c) && !selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
        addedCount++;
      }
    });

    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_FROM_LABEL', { count: addedCount }));
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const selectAllFromAttribute = async () => {
  if (!selectedCustomAttribute.value || !customAttributeValue.value) return;

  isSearching.value = true;
  try {
    let allContacts = [];
    let page = 1;
    let hasMore = true;

    // Ensure we pass the string value, not an object
    const attributeValue = typeof customAttributeValue.value === 'object'
      ? customAttributeValue.value.value || customAttributeValue.value
      : customAttributeValue.value;

    const queryPayload = {
      payload: [
        {
          attribute_key: selectedCustomAttribute.value,
          filter_operator: 'equal_to',
          values: [attributeValue],
          custom_attribute_type: 'contact_attribute',
        },
      ],
    };

    while (hasMore && page <= 50) { // Limit to 50 pages
      const response = await ContactAPI.filter(page, 'name', queryPayload);
      const data = response.data;
      const contactsList = data.payload || [];
      allContacts = [...allContacts, ...contactsList];
      // Continue if we got results and there might be more pages
      const totalPages = data.meta?.total_pages;
      hasMore = contactsList.length > 0 && (totalPages ? page < totalPages : true);
      page++;
    }

    // Add all contacts that are not already in the list
    let addedCount = 0;
    allContacts.forEach(c => {
      if (!isContactInList(c) && !selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
        addedCount++;
      }
    });

    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_FROM_ATTRIBUTE', { count: addedCount }));
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const addSelectedContacts = async () => {
  if (selectedContacts.value.length === 0) return;

  isAddingContacts.value = true;
  try {
    await store.dispatch('contactLists/addContacts', {
      listId: props.list.id,
      contactIds: selectedContacts.value,
    });
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_MULTIPLE_SUCCESS', { count: selectedContacts.value.length }));
    selectedContacts.value = [];
    searchQuery.value = '';
    searchResults.value = [];
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isAddingContacts.value = false;
  }
};

const addContact = async (contact) => {
  try {
    await store.dispatch('contactLists/addContacts', {
      listId: props.list.id,
      contactIds: [contact.id],
    });
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_SUCCESS'));
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  }
};

const removeContact = async (contact) => {
  try {
    await store.dispatch('contactLists/removeContacts', {
      listId: props.list.id,
      contactIds: [contact.id],
    });
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_SUCCESS'));
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  }
};

const isRemovingAll = ref(false);

const removeAllContacts = async () => {
  if (!props.list?.contacts_count || props.list.contacts_count === 0) return;

  isRemovingAll.value = true;
  try {
    // Fetch all contact IDs from all pages
    let allContactIds = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await store.dispatch('contactLists/getContacts', {
        listId: props.list.id,
        page,
      });
      const contactsList = response.contacts || [];
      allContactIds = [...allContactIds, ...contactsList.map(c => c.id)];
      hasMore = response.meta?.current_page < response.meta?.total_pages;
      page++;
    }

    if (allContactIds.length > 0) {
      await store.dispatch('contactLists/removeContacts', {
        listId: props.list.id,
        contactIds: allContactIds,
      });
      useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_ALL_SUCCESS', { count: allContactIds.length }));
      await fetchContacts(1);
      await store.dispatch('contactLists/get');
    }
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isRemovingAll.value = false;
  }
};

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    fetchContacts(page);
  }
};

const goToSearchPage = (page) => {
  if (page >= 1 && page <= searchTotalPages.value) {
    searchContacts(page);
  }
};

const clearSelection = () => {
  selectedContacts.value = [];
};

watch([searchQuery, selectedLabel], () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

watch([selectedCustomAttribute, customAttributeValue], () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

// Reset custom attribute value when attribute changes
watch(selectedCustomAttribute, () => {
  customAttributeValue.value = '';
});

onMounted(() => {
  dialogRef.value?.open();
  fetchContacts();
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
  // Initial search to show contacts
  searchContacts(1);
});

const handleClose = () => {
  dialogRef.value?.close();
  emit('close');
};

const isContactInList = (contact) => {
  return contacts.value.some(c => c.id === contact.id);
};

const isContactSelected = (contactId) => {
  return selectedContacts.value.includes(contactId);
};

// Import Excel functionality
const openImportDialog = () => {
  importFileRef.value?.click();
};

const formatPhoneNumber = (phone) => {
  if (!phone) return null;

  // Remove all non-numeric characters
  let cleaned = String(phone).replace(/\D/g, '');

  // Remove DDI 55 if present to normalize
  if (cleaned.startsWith('55') && cleaned.length > 11) {
    cleaned = cleaned.substring(2);
  }

  // Now we should have DDD + number (10 or 11 digits)
  if (cleaned.length < 10 || cleaned.length > 11) {
    return null; // Invalid number
  }

  // Extract DDD (first 2 digits)
  const ddd = parseInt(cleaned.substring(0, 2), 10);
  let number = cleaned.substring(2);

  // Apply 9-digit rule based on DDD
  if (ddd <= 30) {
    // DDDs 11-30: must have 9 at the beginning
    if (number.length === 8) {
      // Add 9 if missing
      number = '9' + number;
    }
  } else {
    // DDDs > 30: remove extra 9 if present (9 digits starting with 9)
    if (number.length === 9 && number.startsWith('9')) {
      // Keep it as is - all mobile numbers in Brazil now have 9 digits
      // Actually, since 2016 all Brazil has 9 digits, so we keep it
    } else if (number.length === 8) {
      // Add 9 for mobile numbers
      number = '9' + number;
    }
  }

  // Final format: +55 + DDD + number
  return `+55${ddd.toString().padStart(2, '0')}${number}`;
};

const handleImportExcel = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  isImporting.value = true;

  try {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON (column A = name, column B = phone)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Skip header row if present and process data
        const contactsToCreate = [];
        const startRow = jsonData[0]?.[0]?.toString().toLowerCase().includes('nome') ? 1 : 0;

        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          const name = row[0]?.toString().trim();
          const phone = formatPhoneNumber(row[1]);

          if (name && phone) {
            contactsToCreate.push({ name, phone_number: phone });
          }
        }

        if (contactsToCreate.length === 0) {
          useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_NO_CONTACTS'));
          return;
        }

        // Create contacts and add to list
        let created = 0;
        let errors = 0;
        const createdContactIds = [];

        for (const contactData of contactsToCreate) {
          try {
            // Try to create contact
            const response = await ContactAPI.create(contactData);
            if (response.data?.payload?.contact?.id) {
              createdContactIds.push(response.data.payload.contact.id);
              created++;
            }
          } catch (error) {
            // If contact already exists, try to find by phone
            if (error.response?.status === 422) {
              try {
                const searchResponse = await ContactAPI.search(
                  contactData.phone_number,
                  1,
                  'phone_number'
                );
                const existingContact = searchResponse.data.payload?.[0];
                if (existingContact?.id) {
                  createdContactIds.push(existingContact.id);
                  created++;
                } else {
                  errors++;
                }
              } catch {
                errors++;
              }
            } else {
              errors++;
            }
          }
        }

        // Add all created contacts to the list
        if (createdContactIds.length > 0) {
          await store.dispatch('contactLists/addContacts', {
            listId: props.list.id,
            contactIds: createdContactIds,
          });
        }

        await fetchContacts(currentPage.value);
        await store.dispatch('contactLists/get');

        useAlert(
          t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_SUCCESS', {
            created,
            errors,
            total: contactsToCreate.length
          })
        );
      } catch (error) {
        useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR'));
      } finally {
        isImporting.value = false;
        // Reset file input
        if (importFileRef.value) {
          importFileRef.value.value = '';
        }
      }
    };

    reader.onerror = () => {
      useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR'));
      isImporting.value = false;
    };

    reader.readAsArrayBuffer(file);
  } catch (error) {
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR'));
    isImporting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="2xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <!-- Hidden file input for Excel import -->
      <input
        ref="importFileRef"
        type="file"
        accept=".xlsx,.xls,.csv"
        class="hidden"
        @change="handleImportExcel"
      />

      <!-- Search and filter section -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.SEARCH_LABEL') }}
          </label>
          <Button
            type="button"
            size="xs"
            variant="faded"
            color="blue"
            icon="i-lucide-file-spreadsheet"
            :label="t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_EXCEL')"
            :is-loading="isImporting"
            @click.stop.prevent="openImportDialog"
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="searchQuery"
            :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SEARCH_PLACEHOLDER')"
            icon="i-lucide-search"
          />

          <div class="flex gap-2">
            <ComboBox
              v-model="selectedLabel"
              :options="labelOptions"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_LABEL')"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
            />
            <Button
              v-if="selectedLabel"
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-check-check"
              :title="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_LABEL')"
              @click.stop.prevent="selectAllFromLabel"
            />
          </div>
        </div>

        <!-- Custom Attribute Filter -->
        <div
          v-if="customAttributes.length > 0"
          class="grid grid-cols-2 gap-3"
        >
          <ComboBox
            v-model="selectedCustomAttribute"
            :options="customAttributeOptions"
            :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ATTRIBUTE')"
            class="[&>div>button]:bg-n-alpha-black2"
          />

          <div
            v-if="selectedCustomAttribute"
            class="flex gap-2"
          >
            <ComboBox
              v-if="isListAttribute"
              v-model="customAttributeValue"
              :options="listAttributeOptions"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_VALUE')"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
            />
            <Input
              v-else
              v-model="customAttributeValue"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.ATTRIBUTE_VALUE')"
              class="flex-1"
            />
            <Button
              v-if="customAttributeValue"
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-check-check"
              :title="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_ATTRIBUTE')"
              @click.stop.prevent="selectAllFromAttribute"
            />
          </div>
        </div>

        <!-- Selection actions bar -->
        <div
          v-if="selectedCount > 0"
          class="flex items-center justify-between p-3 bg-n-alpha-2 rounded-lg"
        >
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_COUNT', { count: selectedCount }) }}
          </span>
          <div class="flex gap-2">
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              :label="t('CONTACT_LISTS.CONTACTS_DIALOG.CLEAR_SELECTION')"
              @click.stop.prevent="clearSelection"
            />
            <Button
              type="button"
              size="xs"
              color="blue"
              icon="i-lucide-plus"
              :label="t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_SELECTED')"
              :is-loading="isAddingContacts"
              @click.stop.prevent="addSelectedContacts"
            />
          </div>
        </div>

        <!-- Search results -->
        <div
          v-if="isSearching"
          class="flex items-center justify-center py-4"
        >
          <Spinner size="small" />
        </div>

        <div
          v-else-if="searchResults.length > 0"
          class="border border-n-weak rounded-lg"
        >
          <!-- Select all header -->
          <div class="flex items-center justify-between p-3 border-b border-n-weak bg-n-alpha-1">
            <div class="flex items-center gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="allSearchSelected"
                  :indeterminate="someSearchSelected"
                  class="w-4 h-4 rounded border-n-weak text-n-blue-text focus:ring-n-blue-text"
                  @change="toggleSelectAllSearch"
                />
                <span class="text-sm text-n-slate-11">
                  {{ t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_PAGE') }}
                </span>
              </label>
              <span class="text-xs text-n-slate-10">
                ({{ searchTotalCount }} {{ t('CONTACT_LISTS.CONTACTS_DIALOG.TOTAL_FOUND') }})
              </span>
            </div>
            <Button
              v-if="searchTotalCount > searchResults.length"
              type="button"
              size="xs"
              variant="link"
              color="blue"
              :label="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_TOTAL', { count: searchTotalCount })"
              :is-loading="isSearching"
              @click.stop.prevent="selectAllFromSearch"
            />
          </div>

          <div class="max-h-48 overflow-auto">
            <div
              v-for="contact in searchResults"
              :key="contact.id"
              class="flex items-center gap-3 p-3 hover:bg-n-alpha-2 border-b border-n-weak last:border-b-0"
              :class="{ 'bg-n-alpha-1': isContactSelected(contact.id) }"
            >
              <input
                v-if="!isContactInList(contact)"
                type="checkbox"
                :checked="isContactSelected(contact.id)"
                class="w-4 h-4 rounded border-n-weak text-n-blue-text focus:ring-n-blue-text"
                @change="toggleContactSelection(contact.id)"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ contact.name }}
                </p>
                <p class="text-xs text-n-slate-10 truncate">
                  {{ contact.phone_number || contact.email }}
                </p>
              </div>
              <Button
                v-if="!isContactInList(contact) && !isContactSelected(contact.id)"
                type="button"
                size="xs"
                variant="faded"
                color="slate"
                icon="i-lucide-plus"
                @click.stop.prevent="addContact(contact)"
              />
              <span
                v-else-if="isContactInList(contact)"
                class="text-xs text-n-slate-10 px-2 py-1 bg-n-alpha-2 rounded"
              >
                {{ t('CONTACT_LISTS.CONTACTS_DIALOG.ALREADY_ADDED') }}
              </span>
            </div>
          </div>

          <!-- Search pagination -->
          <div
            v-if="searchTotalPages > 1"
            class="flex items-center justify-center gap-2 p-3 border-t border-n-weak"
          >
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-left"
              :disabled="searchCurrentPage === 1"
              @click.stop.prevent="goToSearchPage(searchCurrentPage - 1)"
            />
            <span class="text-sm text-n-slate-11">
              {{ searchCurrentPage }} / {{ searchTotalPages }}
            </span>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-right"
              :disabled="searchCurrentPage === searchTotalPages"
              @click.stop.prevent="goToSearchPage(searchCurrentPage + 1)"
            />
          </div>
        </div>

        <div
          v-else-if="!isSearching && (searchQuery || selectedLabel || (selectedCustomAttribute && customAttributeValue))"
          class="text-center py-4 text-sm text-n-slate-10"
        >
          {{ t('CONTACT_LISTS.CONTACTS_DIALOG.NO_RESULTS') }}
        </div>
      </div>

      <!-- Divider -->
      <hr class="border-n-weak" />

      <!-- Current contacts in list -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.CURRENT_CONTACTS') }}
            ({{ contacts.length > 0 ? (list?.contacts_count || contacts.length) : 0 }})
          </label>
          <Button
            v-if="contacts.length > 0"
            type="button"
            size="xs"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash-2"
            :label="t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_ALL')"
            :is-loading="isRemovingAll"
            @click.stop.prevent="removeAllContacts"
          />
        </div>

        <div
          v-if="isLoading"
          class="flex items-center justify-center py-8"
        >
          <Spinner />
        </div>

        <div
          v-else-if="contacts.length > 0"
          class="border border-n-weak rounded-lg"
        >
          <div
            v-for="contact in contacts"
            :key="contact.id"
            class="flex items-center justify-between p-3 hover:bg-n-alpha-2 border-b border-n-weak last:border-b-0"
          >
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ contact.name }}
              </p>
              <p class="text-xs text-n-slate-10 truncate">
                {{ contact.phone_number || contact.email }}
              </p>
            </div>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="ruby"
              icon="i-lucide-trash-2"
              @click.stop.prevent="removeContact(contact)"
            />
          </div>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="flex items-center justify-center gap-2 p-3 border-t border-n-weak"
          >
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-left"
              :disabled="currentPage === 1"
              @click.stop.prevent="goToPage(currentPage - 1)"
            />
            <span class="text-sm text-n-slate-11">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-right"
              :disabled="currentPage === totalPages"
              @click.stop.prevent="goToPage(currentPage + 1)"
            />
          </div>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center py-8 text-center"
        >
          <span class="i-lucide-users text-3xl text-n-slate-10 mb-2" />
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.NO_CONTACTS') }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('CONTACT_LISTS.FORM.CANCEL')"
          @click.stop.prevent="handleClose"
        />
      </div>
    </template>
  </Dialog>
</template>
